import React, { useState, useCallback, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../../config';
import { getAuthHeaders } from '../../api/auth';
import './CheckInTerminal.css';
import logoKirchhoff from './Logo Kirchhoff.png';
import VirtualKeyboard from './VirtualKeyboard';

// Hide AIpad footer when this component is mounted
function useHideAIpadFooter() {
  useEffect(() => {
    // If running in iframe (Feed container), target parent window body
    const targetBody = window.self !== window.top && window.parent
      ? window.parent.document.body
      : document.body;

    targetBody.classList.add('hide-aipad-footer');

    return () => {
      targetBody.classList.remove('hide-aipad-footer');
    };
  }, []);
}

type Language = 'de' | 'en' | 'fr' | 'it' | 'pl' | 'pt' | 'tr' | 'ru' | 'es' | 'uk' | 'ro' | 'bg' | 'sk' | 'be';

interface LanguageOption {
  code: Language;
  name: string;
  flag: string;
}

interface CheckInFormData {
  licensePlate: string;
  driver_firstName: string;
  driver_lastName: string;
  driver_mobile: string;
  carrier: string;
  supplier: string;
  purpose: string[]; // Multi-select: Array von Zwecken
}

const LANGUAGES: LanguageOption[] = [
  { code: 'de', name: 'Deutsch', flag: 'https://flagcdn.com/w160/de.png' },
  { code: 'en', name: 'English', flag: 'https://flagcdn.com/w160/gb.png' },
  { code: 'fr', name: 'Français', flag: 'https://flagcdn.com/w160/fr.png' },
  { code: 'it', name: 'Italiano', flag: 'https://flagcdn.com/w160/it.png' },
  { code: 'pl', name: 'Polski', flag: 'https://flagcdn.com/w160/pl.png' },
  { code: 'pt', name: 'Português', flag: 'https://flagcdn.com/w160/pt.png' },
  { code: 'tr', name: 'Türkçe', flag: 'https://flagcdn.com/w160/tr.png' },
  { code: 'ru', name: 'Русский', flag: 'https://flagcdn.com/w160/ru.png' },
  { code: 'es', name: 'Español', flag: 'https://flagcdn.com/w160/es.png' },
  { code: 'uk', name: 'Українська', flag: 'https://flagcdn.com/w160/ua.png' },
  { code: 'ro', name: 'Română', flag: 'https://flagcdn.com/w160/ro.png' },
  { code: 'bg', name: 'Български', flag: 'https://flagcdn.com/w160/bg.png' },
  { code: 'sk', name: 'Slovenčina', flag: 'https://flagcdn.com/w160/sk.png' },
  { code: 'be', name: 'Беларуская', flag: 'https://flagcdn.com/w160/by.png' },
];

const TRANSLATIONS = {
  safetyTitle: {
    de: 'Sicherheitshinweise',
    en: 'Safety Instructions',
    fr: 'Consignes de sécurité',
    it: 'Istruzioni di sicurezza',
    pl: 'Instrukcje bezpieczeństwa',
    pt: 'Instruções de segurança',
    tr: 'Güvenlik talimatları',
    ru: 'Инструкции по безопасности',
    es: 'Instrucciones de seguridad',
    uk: 'Інструкції з безпеки',
    ro: 'Instrucțiuni de siguranță',
    bg: 'Инструкции за безопасност',
    sk: 'Bezpečnostné pokyny',
    be: 'Інструкцыі па бяспецы',
  },
  safetyNotice: {
    de: 'Bitte beachten Sie folgende, wichtige Hinweise:\nAuf dem Firmengelände gilt die StVO sowie die Pflicht zum Tragen einer Warnweste und Sicherheitsschuhen!\n\nEs ist Schrittgeschwindigkeit zu fahren sowie auf Staplerverkehr zu achten!\n\nDie gesetzlichen Anforderungen an die Ladungssicherung (§§ 22 StVO) sind zwingend einzuhalten.',
    en: 'Please note the following important information:\nTraffic regulations apply on company premises as well as the obligation to wear a high-visibility vest and safety shoes!\n\nWalking speed must be observed and attention must be paid to forklift traffic!\n\nThe legal requirements for load securing (§§ 22 StVO) must be strictly adhered to.',
    fr: 'Veuillez noter les informations importantes suivantes:\nLe code de la route s\'applique sur le site de l\'entreprise ainsi que l\'obligation de porter un gilet haute visibilité et des chaussures de sécurité!\n\nLa vitesse au pas doit être respectée et il faut faire attention à la circulation des chariots élévateurs!\n\nLes exigences légales en matière d\'arrimage de la charge (§§ 22 StVO) doivent être strictement respectées.',
    it: 'Si prega di notare le seguenti informazioni importanti:\nNel sito aziendale si applicano le norme del codice della strada e l\'obbligo di indossare gilet ad alta visibilità e scarpe di sicurezza!\n\nÈ necessario procedere a passo d\'uomo e prestare attenzione al traffico dei carrelli elevatori!\n\nI requisiti di legge per il fissaggio del carico (§§ 22 StVO) devono essere rigorosamente rispettati.',
    pl: 'Proszę zwrócić uwagę na następujące ważne informacje:\nNa terenie firmy obowiązują przepisy ruchu drogowego oraz obowiązek noszenia kamizelki ostrzegawczej i obuwia ochronnego!\n\nNależy poruszać się z prędkością krokową i zwracać uwagę na ruch wózków widłowych!\n\nWymagania prawne dotyczące zabezpieczenia ładunku (§§ 22 StVO) muszą być bezwzględnie przestrzegane.',
    pt: 'Por favor, note as seguintes informações importantes:\nNas instalações da empresa aplicam-se as regras de trânsito, bem como a obrigação de usar colete refletivo e sapatos de segurança!\n\nDeve-se conduzir à velocidade de passo e prestar atenção ao tráfego de empilhadeiras!\n\nOs requisitos legais para fixação de carga (§§ 22 StVO) devem ser rigorosamente cumpridos.',
    tr: 'Lütfen aşağıdaki önemli bilgilere dikkat edin:\nŞirket arazisinde trafik kuralları ve yüksek görünürlüklü yelek ile güvenlik ayakkabısı giyme zorunluluğu geçerlidir!\n\nYürüyüş hızında sürülmeli ve forklift trafiğine dikkat edilmelidir!\n\nYük emniyeti için yasal gereksinimler (§§ 22 StVO) kesinlikle uyulmalıdır.',
    ru: 'Пожалуйста, обратите внимание на следующую важную информацию:\nНа территории предприятия действуют правила дорожного движения, а также обязательное ношение светоотражающего жилета и защитной обуви!\n\nНеобходимо двигаться со скоростью пешехода и обращать внимание на движение погрузчиков!\n\nЗаконодательные требования по креплению груза (§§ 22 StVO) должны строго соблюдаться.',
    es: 'Por favor, tenga en cuenta la siguiente información importante:\nEn las instalaciones de la empresa se aplican las normas de tráfico, así como la obligación de usar chaleco de alta visibilidad y zapatos de seguridad!\n\nSe debe conducir a velocidad de paso y prestar atención al tráfico de carretillas elevadoras!\n\nLos requisitos legales para el aseguramiento de la carga (§§ 22 StVO) deben cumplirse estrictamente.',
    uk: 'Будь ласка, зверніть увагу на наступну важливу інформацію:\nНа території підприємства діють правила дорожнього руху, а також обов\'язкове носіння світловідбивного жилета та захисного взуття!\n\nНеобхідно рухатися зі швидкістю пішохода та звертати увагу на рух навантажувачів!\n\nЗаконодавчі вимоги щодо кріплення вантажу (§§ 22 StVO) повинні суворо дотримуватися.',
    ro: 'Vă rugăm să rețineți următoarele informații importante:\nPe terenul companiei se aplică regulile de circulație, precum și obligația de a purta vestă reflectorizantă și încălțăminte de protecție!\n\nTrebuie să conduceți cu viteză de mers și să fiți atenți la traficul de stivuitoare!\n\nCerințele legale privind asigurarea încărcăturii (§§ 22 StVO) trebuie respectate cu strictețe.',
    bg: 'Моля, обърнете внимание на следната важна информация:\nНа територията на предприятието се прилагат правилата за движение, както и задължението за носене на светлоотразителна жилетка и защитни обувки!\n\nТрябва да се движите с крачна скорост и да обръщате внимание на движението на мотокари!\n\nЗаконовите изисквания за закрепване на товара (§§ 22 StVO) трябва да се спазват стриктно.',
    sk: 'Prosím, všimnite si nasledujúce dôležité informácie:\nNa areáli spoločnosti platia pravidlá cestnej premávky, ako aj povinnosť nosiť reflexnú vestu a bezpečnostnú obuv!\n\nMusí sa jazdiť krokovou rýchlosťou a dávať pozor na pohyb vysokozdvižných vozíkov!\n\nZákonné požiadavky na zaistenie nákladu (§§ 22 StVO) musia byť prísne dodržané.',
    be: 'Калі ласка, звярніце ўвагу на наступную важную інфармацыю:\nНа тэрыторыі прадпрыемства дзейнічаюць правілы дарожнага руху, а таксама абавязковае насенне светлаадбівальнага жылета і абарончага абутку!\n\nНеабходна рухацца са хуткасцю пешахода і звяртаць увагу на рух пагрузчыкаў!\n\nЗаканадаўчыя патрабаванні па мацаванню грузу (§§ 22 StVO) павінны строга выконвацца.',
  },
  confirmButton: {
    de: 'Bestätigen',
    en: 'Confirm',
    fr: 'Confirmer',
    it: 'Confermare',
    pl: 'Potwierdzić',
    pt: 'Confirmar',
    tr: 'Onaylamak',
    ru: 'Подтвердить',
    es: 'Confirmar',
    uk: 'Підтвердити',
    ro: 'Confirma',
    bg: 'Потвърди',
    sk: 'Potvrdiť',
    be: 'Пацвердзіць',
  },
  formTitle: {
    de: 'Check-In Daten',
    en: 'Check-In Data',
    fr: "Données d'enregistrement",
    it: 'Dati di check-in',
    pl: 'Dane zameldowania',
    pt: 'Dados de check-in',
    tr: 'Giriş verileri',
    ru: 'Данные регистрации',
    es: 'Datos de registro',
    uk: 'Дані реєстрації',
    ro: 'Date de înregistrare',
    bg: 'Данни за регистрация',
    sk: 'Údaje o registrácii',
    be: 'Даныя рэгістрацыі',
  },
  licensePlate: {
    de: 'Kennzeichen',
    en: 'License Plate',
    fr: "Plaque d'immatriculation",
    it: 'Targa',
    pl: 'Tablica rejestracyjna',
    pt: 'Placa',
    tr: 'Plaka',
    ru: 'Номерной знак',
    es: 'Matrícula',
    uk: 'Номерний знак',
    ro: 'Număr de înmatriculare',
    bg: 'Регистрационен номер',
    sk: 'Evidenčné číslo',
    be: 'Нумарны знак',
  },
  licensePlateSuffix: {
    de: '(Zugmaschine)',
    en: '(Tractor)',
    fr: '(Tracteur)',
    it: '(Trattore)',
    pl: '(Ciągnik)',
    pt: '(Trator)',
    tr: '(Çekici)',
    ru: '(Тягач)',
    es: '(Tractor)',
    uk: '(Тягач)',
    ro: '(Tractor)',
    bg: '(Влекач)',
    sk: '(Traktor)',
    be: '(Цягач)',
  },
  lastName: {
    de: 'Nachname',
    en: 'Last Name',
    fr: 'Nom de famille',
    it: 'Cognome',
    pl: 'Nazwisko',
    pt: 'Sobrenome',
    tr: 'Soyadı',
    ru: 'Фамилия',
    es: 'Apellido',
    uk: 'Прізвище',
    ro: 'Nume de familie',
    bg: 'Фамилия',
    sk: 'Priezvisko',
    be: 'Прозвішча',
  },
  firstName: {
    de: 'Vorname',
    en: 'First Name',
    fr: 'Prénom',
    it: 'Nome',
    pl: 'Imię',
    pt: 'Nome',
    tr: 'Ad',
    ru: 'Имя',
    es: 'Nombre',
    uk: 'Ім\'я',
    ro: 'Prenume',
    bg: 'Име',
    sk: 'Meno',
    be: 'Імя',
  },
  carrier: {
    de: 'Spedition',
    en: 'Carrier',
    fr: 'Transporteur',
    it: 'Vettore',
    pl: 'Przewoźnik',
    pt: 'Transportadora',
    tr: 'Taşıyıcı',
    ru: 'Перевозчик',
    es: 'Transportista',
    uk: 'Перевізник',
    ro: 'Transportator',
    bg: 'Превозвач',
    sk: 'Prepravca',
    be: 'Перавозчык',
  },
  supplier: {
    de: 'Lieferant',
    en: 'Supplier',
    fr: 'Fournisseur',
    it: 'Fornitore',
    pl: 'Dostawca',
    pt: 'Fornecedor',
    tr: 'Tedarikçi',
    ru: 'Поставщик',
    es: 'Proveedor',
    uk: 'Постачальник',
    ro: 'Furnizor',
    bg: 'Доставчик',
    sk: 'Dodávateľ',
    be: 'Пастаўшчык',
  },
  mobile: {
    de: 'Mobilnummer',
    en: 'Mobile Number',
    fr: 'Numéro de portable',
    it: 'Numero di cellulare',
    pl: 'Numer telefonu komórkowego',
    pt: 'Número de celular',
    tr: 'Cep telefonu numarası',
    ru: 'Номер мобильного телефона',
    es: 'Número de móvil',
    uk: 'Номер мобільного телефону',
    ro: 'Număr de mobil',
    bg: 'Мобилен номер',
    sk: 'Mobilné číslo',
    be: 'Нумар мабільнага тэлефона',
  },
  purpose: {
    de: 'Ziel / Zweck',
    en: 'Purpose',
    fr: 'Objectif',
    it: 'Scopo',
    pl: 'Cel',
    pt: 'Objetivo',
    tr: 'Amaç',
    ru: 'Цель',
    es: 'Propósito',
    uk: 'Мета',
    ro: 'Scop',
    bg: 'Цел',
    sk: 'Účel',
    be: 'Мэта',
  },
  purposeShipping: {
    de: 'Versand',
    en: 'Shipping',
    fr: 'Expédition',
    it: 'Spedizione',
    pl: 'Wysyłka',
    pt: 'Envio',
    tr: 'Sevkiyat',
    ru: 'Отправка',
    es: 'Envío',
    uk: 'Відправлення',
    ro: 'Expediere',
    bg: 'Изпращане',
    sk: 'Odoslanie',
    be: 'Адпраўка',
  },
  purposeReceipt: {
    de: 'Wareneingang',
    en: 'Goods Receipt',
    fr: 'Réception de marchandises',
    it: 'Ricevimento merci',
    pl: 'Odbiór towarów',
    pt: 'Recebimento de mercadorias',
    tr: 'Mal kabul',
    ru: 'Приемка товаров',
    es: 'Recepción de mercancías',
    uk: 'Прийом товарів',
    ro: 'Primire mărfuri',
    bg: 'Приемане на стоки',
    sk: 'Príjem tovaru',
    be: 'Прыёмка тавараў',
  },
  purposeEmpties: {
    de: 'Leergut',
    en: 'Empties',
    fr: 'Emballages vides',
    it: 'Vuoti',
    pl: 'Opakowania zwrotne',
    pt: 'Embalagens vazias',
    tr: 'Boş ambalaj',
    ru: 'Пустая тара',
    es: 'Envases vacíos',
    uk: 'Порожня тара',
    ro: 'Ambalaje goale',
    bg: 'Празна опаковка',
    sk: 'Prázdne obaly',
    be: 'Пустая тара',
  },
  purposeOther: {
    de: 'Sonstiges',
    en: 'Other',
    fr: 'Autre',
    it: 'Altro',
    pl: 'Inne',
    pt: 'Outro',
    tr: 'Diğer',
    ru: 'Другое',
    es: 'Otro',
    uk: 'Інше',
    ro: 'Altul',
    bg: 'Друго',
    sk: 'Iné',
    be: 'Іншае',
  },
  submitButton: {
    de: 'Abschließen',
    en: 'Complete',
    fr: 'Terminer',
    it: 'Completare',
    pl: 'Zakończyć',
    pt: 'Concluir',
    tr: 'Tamamla',
    ru: 'Завершить',
    es: 'Completar',
    uk: 'Завершити',
    ro: 'Finalizare',
    bg: 'Завърши',
    sk: 'Dokončiť',
    be: 'Завяршыць',
  },
  successMessage: {
    de: 'Vielen Dank! Ihr Check-In war erfolgreich.',
    en: 'Thank you! Your check-in was successful.',
    fr: 'Merci! Votre enregistrement a réussi.',
    it: 'Grazie! Il tuo check-in è andato a buon fine.',
    pl: 'Dziękujemy! Twoje zameldowanie powiodło się.',
    pt: 'Obrigado! Seu check-in foi bem-sucedido.',
    tr: 'Teşekkürler! Girişiniz başarılı oldu.',
    ru: 'Спасибо! Ваша регистрация прошла успешно.',
    es: '¡Gracias! Su registro fue exitoso.',
    uk: 'Дякуємо! Ваша реєстрація пройшла успішно.',
    ro: 'Mulțumesc! Înregistrarea dvs. a avut succes.',
    bg: 'Благодаря! Вашата регистрация беше успешна.',
    sk: 'Ďakujeme! Vaša registrácia bola úspešná.',
    be: 'Дзякуй! Ваша рэгістрацыя прайшла паспяхова.',
  },
  cancelButton: {
    de: 'Abbrechen',
    en: 'Cancel',
    fr: 'Annuler',
    it: 'Annulla',
    pl: 'Anuluj',
    pt: 'Cancelar',
    tr: 'İptal',
    ru: 'Отменить',
    es: 'Cancelar',
    uk: 'Скасувати',
    ro: 'Anulare',
    bg: 'Отказ',
    sk: 'Zrušiť',
    be: 'Адмяніць',
  },
  cancelConfirmTitle: {
    de: 'Abbrechen?',
    en: 'Cancel?',
    fr: 'Annuler?',
    it: 'Annullare?',
    pl: 'Anulować?',
    pt: 'Cancelar?',
    tr: 'İptal et?',
    ru: 'Отменить?',
    es: '¿Cancelar?',
    uk: 'Скасувати?',
    ro: 'Anulare?',
    bg: 'Отказ?',
    sk: 'Zrušiť?',
    be: 'Адмяніць?',
  },
  cancelConfirmMessage: {
    de: 'Möchten Sie den Check-In wirklich abbrechen?',
    en: 'Do you really want to cancel the check-in?',
    fr: "Voulez-vous vraiment annuler l'enregistrement?",
    it: 'Vuoi davvero annullare il check-in?',
    pl: 'Czy na pewno chcesz anulować zameldowanie?',
    pt: 'Você realmente deseja cancelar o check-in?',
    tr: 'Girişi gerçekten iptal etmek istiyor musunuz?',
    ru: 'Вы действительно хотите отменить регистрацию?',
    es: '¿Realmente desea cancelar el registro?',
    uk: 'Ви дійсно хочете скасувати реєстрацію?',
    ro: 'Doriți cu adevărat să anulați înregistrarea?',
    bg: 'Наистина ли искате да откажете регистрацията?',
    sk: 'Naozaj chcete zrušiť registráciu?',
    be: 'Вы сапраўды хочаце адмяніць рэгістрацыю?',
  },
  cancelConfirmYes: {
    de: 'Ja, abbrechen',
    en: 'Yes, cancel',
    fr: 'Oui, annuler',
    it: 'Sì, annulla',
    pl: 'Tak, anuluj',
    pt: 'Sim, cancelar',
    tr: 'Evet, iptal et',
    ru: 'Да, отменить',
    es: 'Sí, cancelar',
    uk: 'Так, скасувати',
    ro: 'Da, anulare',
    bg: 'Да, откажи',
    sk: 'Áno, zrušiť',
    be: 'Так, адмяніць',
  },
  cancelConfirmNo: {
    de: 'Nein, fortfahren',
    en: 'No, continue',
    fr: 'Non, continuer',
    it: 'No, continua',
    pl: 'Nie, kontynuuj',
    pt: 'Não, continuar',
    tr: 'Hayır, devam et',
    ru: 'Нет, продолжить',
    es: 'No, continuar',
    uk: 'Ні, продовжити',
    ro: 'Nu, continuare',
    bg: 'Не, продължи',
    sk: 'Nie, pokračovať',
    be: 'Не, працягнуць',
  },
};

type Screen = 'language' | 'safety' | 'form' | 'success';

const INITIAL_FORM_DATA: CheckInFormData = {
  licensePlate: '',
  driver_firstName: '',
  driver_lastName: '',
  driver_mobile: '',
  carrier: '',
  supplier: '',
  purpose: [],
};

export default function CheckInTerminal() {
  // Hide AIpad footer while this component is mounted
  useHideAIpadFooter();

  const [currentScreen, setCurrentScreen] = useState<Screen>('language');
  const [selectedLanguage, setSelectedLanguage] = useState<Language>('de');
  const [formData, setFormData] = useState<CheckInFormData>(INITIAL_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [hideHeader, setHideHeader] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [safetyCountdown, setSafetyCountdown] = useState(5);
  const [isInIframe, setIsInIframe] = useState(false);

  // Virtual Keyboard State
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [activeInputField, setActiveInputField] = useState<keyof CheckInFormData | null>(null);
  const activeInputRef = useRef<HTMLInputElement | null>(null);

  // Auto-reset interval ref for proper cleanup
  const autoResetIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Purpose dropdown state
  const [isPurposeOpen, setIsPurposeOpen] = useState(false);

  // Determine keyboard type based on active field
  const getInputType = (): 'text' | 'licensePlate' | 'numeric' => {
    if (activeInputField === 'licensePlate') return 'licensePlate';
    if (activeInputField === 'driver_mobile') return 'numeric';
    return 'text';
  };

  // Detect if running in iframe
  useEffect(() => {
    const inIframe = window.self !== window.top;
    setIsInIframe(inIframe);
  }, []);

  // Check URL parameter for fullscreen mode
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fullscreen = params.get('fullscreen');
    if (fullscreen === 'true' || fullscreen === '1') {
      setHideHeader(true);
      // Hide header/navigation if it exists
      const header = document.querySelector('header');
      const nav = document.querySelector('nav');
      if (header) header.style.display = 'none';
      if (nav) nav.style.display = 'none';
    }

    return () => {
      // Restore header on unmount
      const header = document.querySelector('header');
      const nav = document.querySelector('nav');
      if (header) header.style.display = '';
      if (nav) nav.style.display = '';
    };
  }, []);

  const t = useCallback(
    (key: keyof typeof TRANSLATIONS): string => {
      return TRANSLATIONS[key][selectedLanguage];
    },
    [selectedLanguage]
  );

  const handleLanguageSelect = (language: Language) => {
    setSelectedLanguage(language);
    setCurrentScreen('safety');
    setSafetyCountdown(5); // Reset countdown when entering safety screen
  };

  // Safety countdown timer
  useEffect(() => {
    if (currentScreen === 'safety' && safetyCountdown > 0) {
      const timer = setTimeout(() => {
        setSafetyCountdown(safetyCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [currentScreen, safetyCountdown]);

  // Auto-reset timer: Returns to language screen after 30s on success screen
  useEffect(() => {
    if (currentScreen === 'success') {
      // Reset progress to 0 when entering success screen
      setProgressPercent(0);

      let elapsed = 0;
      const interval = setInterval(() => {
        elapsed += 100;
        const percent = (elapsed / 30000) * 100;
        setProgressPercent(percent);

        if (elapsed >= 30000) {
          clearInterval(interval);
          resetToStart();
        }
      }, 100);

      // Store interval ID in ref for potential manual cleanup
      autoResetIntervalRef.current = interval;

      // Cleanup function: Clear interval on unmount or screen change
      return () => {
        if (autoResetIntervalRef.current) {
          clearInterval(autoResetIntervalRef.current);
          autoResetIntervalRef.current = null;
        }
      };
    }
  }, [currentScreen]);

  const handleSafetyConfirm = () => {
    setCurrentScreen('form');
  };

  const handleInputChange = (field: keyof CheckInFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Virtual Keyboard Handlers
  const handleInputFocus = (field: keyof CheckInFormData, inputElement: HTMLInputElement) => {
    setActiveInputField(field);
    activeInputRef.current = inputElement;
    setShowKeyboard(true);

    // Scroll input into view after a short delay (keyboard animation)
    setTimeout(() => {
      inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 350);
  };

  const handleInputBlur = () => {
    // Don't close keyboard immediately on blur - let keyboard buttons handle closing
    // This prevents keyboard from closing when clicking keyboard buttons
  };

  const handleKeyboardKeyPress = (key: string) => {
    if (!activeInputField || !activeInputRef.current) return;

    // Purpose field is array, not handled by keyboard
    if (activeInputField === 'purpose') return;

    const input = activeInputRef.current;
    const currentValue = formData[activeInputField] as string;
    const selectionStart = input.selectionStart || 0;
    const selectionEnd = input.selectionEnd || 0;

    let newValue: string;

    if (key === 'Backspace') {
      if (selectionStart === selectionEnd) {
        // Delete single character before cursor
        newValue = currentValue.slice(0, Math.max(0, selectionStart - 1)) + currentValue.slice(selectionEnd);
        setTimeout(() => {
          input.setSelectionRange(selectionStart - 1, selectionStart - 1);
        }, 0);
      } else {
        // Delete selection
        newValue = currentValue.slice(0, selectionStart) + currentValue.slice(selectionEnd);
        setTimeout(() => {
          input.setSelectionRange(selectionStart, selectionStart);
        }, 0);
      }
    } else {
      // Insert character at cursor position
      newValue = currentValue.slice(0, selectionStart) + key + currentValue.slice(selectionEnd);
      setTimeout(() => {
        input.setSelectionRange(selectionStart + key.length, selectionStart + key.length);
      }, 0);
    }

    handleInputChange(activeInputField, newValue);
    input.focus(); // Keep focus on input
  };

  const handleKeyboardClose = () => {
    setShowKeyboard(false);
    setActiveInputField(null);
    activeInputRef.current = null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all required fields
    const requiredFields: (keyof CheckInFormData)[] = [
      'licensePlate',
      'driver_firstName',
      'driver_lastName',
      'driver_mobile',
      'carrier',
      'supplier',
    ];

    const hasEmptyFields = requiredFields.some((field) => {
      const value = formData[field];
      return typeof value === 'string' && !value.trim();
    });

    if (hasEmptyFields || formData.purpose.length === 0) {
      alert('Bitte füllen Sie alle Pflichtfelder aus und wählen Sie mindestens einen Zweck.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit to API (tenantId explicitly set for Kirchhoff)
      const response = await fetch(`${API_BASE_URL}/api/visits`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Send auth cookies
        body: JSON.stringify({
          type: 'visit',
          tenantId: '69031406f09fed7ea2d97585', // Kirchhoff tenant ID
          licensePlate: formData.licensePlate,
          driver_firstName: formData.driver_firstName,
          driver_lastName: formData.driver_lastName,
          driver_mobile: formData.driver_mobile,
          carrier: formData.carrier,
          supplier: formData.supplier,
          purpose: formData.purpose.join(', '), // Array zu kommagetrennte String
          status: 'WAITING',
          area_slug: 'default',
          area_name: 'Standard',
          checkedInAt: new Date().toISOString(),
          terminal_language: selectedLanguage,
          terminal_sourceId: 'terminal-01',
          display_isVisible: true,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit check-in');
      }

      setCurrentScreen('success');
      // Auto-reset timer is now handled by useEffect hook
    } catch (error) {
      console.error('Check-in error:', error);
      alert('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
      setIsSubmitting(false);
    }
  };

  const resetToStart = () => {
    setCurrentScreen('language');
    setSelectedLanguage('de');
    setFormData(INITIAL_FORM_DATA);
    setIsSubmitting(false);
    setProgressPercent(0);
    setShowCancelDialog(false);
    setShowKeyboard(false);
    setActiveInputField(null);
    activeInputRef.current = null;
  };

  const handleCancelClick = () => {
    setShowCancelDialog(true);
  };

  const handleCancelConfirm = () => {
    resetToStart();
  };

  const handleCancelDismiss = () => {
    setShowCancelDialog(false);
  };

  return (
    <div className={`checkin-terminal ${hideHeader ? 'fullscreen-mode' : ''} ${isInIframe ? 'iframe-mode' : ''}`}>
      {/* Language Selection Screen */}
      {currentScreen === 'language' && (
        <div className="screen language-screen">
          <img src={logoKirchhoff} alt="Kirchhoff" className="terminal-logo" />
          <h1 className="screen-title">Welcome / Willkommen / Bienvenue</h1>
          <div className="language-grid">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                className="language-tile"
                onClick={() => handleLanguageSelect(lang.code)}
              >
                <img src={lang.flag} alt={lang.name} className="language-flag" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Safety Notice Screen */}
      {currentScreen === 'safety' && (
        <div className="screen safety-screen">
          <h1 className="screen-title">{t('safetyTitle')}</h1>
          <div className="safety-content">
            <div className="safety-icon">⚠️</div>
            <p className="safety-text">{t('safetyNotice')}</p>
          </div>
          <div className="button-group">
            <button
              className="confirm-button"
              onClick={handleSafetyConfirm}
              disabled={safetyCountdown > 0}
            >
              {safetyCountdown > 0 ? `${t('confirmButton')} (${safetyCountdown}s)` : t('confirmButton')}
            </button>
            <button className="cancel-button-outline" onClick={handleCancelClick}>
              {t('cancelButton')}
            </button>
          </div>
        </div>
      )}

      {/* Form Screen */}
      {currentScreen === 'form' && (
        <div className="screen form-screen">
          <h1 className="screen-title">{t('formTitle')}</h1>
          <form className="checkin-form" onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-field">
                <label>
                  {t('licensePlate')} <span style={{ fontSize: '0.65em', fontWeight: '400', fontStyle: 'italic' }}>{t('licensePlateSuffix')}</span> *
                </label>
                <input
                  type="text"
                  value={formData.licensePlate}
                  onChange={(e) => handleInputChange('licensePlate', e.target.value)}
                  onFocus={(e) => handleInputFocus('licensePlate', e.currentTarget)}
                  onBlur={handleInputBlur}
                  required
                  autoComplete="off"
                />
              </div>

              <div className="form-field">
                <label>{t('lastName')} *</label>
                <input
                  type="text"
                  value={formData.driver_lastName}
                  onChange={(e) => handleInputChange('driver_lastName', e.target.value)}
                  onFocus={(e) => handleInputFocus('driver_lastName', e.currentTarget)}
                  onBlur={handleInputBlur}
                  required
                  autoComplete="off"
                />
              </div>

              <div className="form-field">
                <label>{t('firstName')} *</label>
                <input
                  type="text"
                  value={formData.driver_firstName}
                  onChange={(e) => handleInputChange('driver_firstName', e.target.value)}
                  onFocus={(e) => handleInputFocus('driver_firstName', e.currentTarget)}
                  onBlur={handleInputBlur}
                  required
                  autoComplete="off"
                />
              </div>

              <div className="form-field">
                <label>{t('carrier')} *</label>
                <input
                  type="text"
                  value={formData.carrier}
                  onChange={(e) => handleInputChange('carrier', e.target.value)}
                  onFocus={(e) => handleInputFocus('carrier', e.currentTarget)}
                  onBlur={handleInputBlur}
                  required
                  autoComplete="off"
                />
              </div>

              <div className="form-field">
                <label>{t('supplier')} *</label>
                <input
                  type="text"
                  value={formData.supplier}
                  onChange={(e) => handleInputChange('supplier', e.target.value)}
                  onFocus={(e) => handleInputFocus('supplier', e.currentTarget)}
                  onBlur={handleInputBlur}
                  required
                  autoComplete="off"
                />
              </div>

              <div className="form-field">
                <label>{t('mobile')} *</label>
                <input
                  type="tel"
                  value={formData.driver_mobile}
                  onChange={(e) => handleInputChange('driver_mobile', e.target.value)}
                  onFocus={(e) => handleInputFocus('driver_mobile', e.currentTarget)}
                  onBlur={handleInputBlur}
                  required
                  autoComplete="off"
                />
              </div>

              <div className="form-field form-field-full">
                <label>{t('purpose')} *</label>
                <div style={{ position: 'relative', width: '100%' }}>
                  {/* Dropdown Header */}
                  <div
                    onClick={() => setIsPurposeOpen(!isPurposeOpen)}
                    style={{
                      border: '2px solid #d1d5db',
                      borderRadius: '8px',
                      padding: '0.75rem 1rem',
                      background: '#ffffff',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '1.75rem',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    <span style={{ color: formData.purpose.length === 0 ? '#9ca3af' : '#1a1a1a' }}>
                      {formData.purpose.length === 0
                        ? 'Bitte wählen'
                        : formData.purpose.join(', ')}
                    </span>
                    <span style={{ fontSize: '1.5rem' }}>{isPurposeOpen ? '▲' : '▼'}</span>
                  </div>

                  {/* Dropdown Options */}
                  {isPurposeOpen && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      marginTop: '0.25rem',
                      border: '2px solid #d1d5db',
                      borderRadius: '8px',
                      padding: '0.5rem',
                      background: '#ffffff',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      zIndex: 10,
                    }}>
                      {[
                        { value: 'Versand', label: t('purposeShipping') },
                        { value: 'Empfang', label: t('purposeReceipt') },
                        { value: 'Leergut', label: t('purposeEmpties') },
                        { value: 'Sonstiges', label: t('purposeOther') },
                      ].map((option) => (
                        <label
                          key={option.value}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0.5rem 1rem',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            transition: 'background 0.2s',
                            background: formData.purpose.includes(option.value) ? '#e0f2fe' : 'transparent',
                          }}
                          onMouseEnter={(e) => {
                            if (!formData.purpose.includes(option.value)) {
                              e.currentTarget.style.background = '#f3f4f6';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!formData.purpose.includes(option.value)) {
                              e.currentTarget.style.background = 'transparent';
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={formData.purpose.includes(option.value)}
                            onChange={(e) => {
                              const newPurpose = e.target.checked
                                ? [...formData.purpose, option.value]
                                : formData.purpose.filter((p) => p !== option.value);
                              setFormData({ ...formData, purpose: newPurpose });
                            }}
                            style={{
                              width: '1.5rem',
                              height: '1.5rem',
                              marginRight: '1rem',
                              cursor: 'pointer',
                              accentColor: '#2563eb',
                              flexShrink: 0,
                            }}
                          />
                          <span style={{ fontSize: '1.75rem', fontWeight: '500' }}>{option.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <button type="submit" className="submit-button" disabled={isSubmitting}>
              {isSubmitting ? '...' : t('submitButton')}
            </button>
            <button type="button" className="cancel-button-outline" onClick={handleCancelClick}>
              {t('cancelButton')}
            </button>
          </form>
        </div>
      )}

      {/* Success Screen */}
      {currentScreen === 'success' && (
        <div className="screen success-screen">
          <div className="success-content">
            <div className="success-icon">✅</div>
            <h1 className="success-message">{t('successMessage')}</h1>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progressPercent}%` }} />
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Dialog */}
      {showCancelDialog && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="modal-title">{t('cancelConfirmTitle')}</h2>
            <p className="modal-message">{t('cancelConfirmMessage')}</p>
            <div className="modal-buttons">
              <button className="modal-button modal-button-confirm" onClick={handleCancelConfirm}>
                {t('cancelConfirmYes')}
              </button>
              <button className="modal-button modal-button-dismiss" onClick={handleCancelDismiss}>
                {t('cancelConfirmNo')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Virtual Keyboard */}
      <VirtualKeyboard
        visible={showKeyboard}
        onKeyPress={handleKeyboardKeyPress}
        onClose={handleKeyboardClose}
        inputType={getInputType()}
      />
    </div>
  );
}
